'use client';

import {
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  MapPin,
  User,
  XCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@kit/ui/card';
import { cn } from '@kit/ui/utils';

import type {
  MeetingPriority,
  MeetingRequestedBy,
  MeetingStatus,
  MeetingWithDetails,
} from '../../services/meeting.service';

interface MeetingCardProps {
  meeting: MeetingWithDetails;
  onClick?: () => void;
  onConfirm?: () => void;
  onCancel?: () => void;
  onComplete?: () => void;
  viewAs?: 'organization' | 'dancer';
  className?: string;
}

/**
 * Card component for displaying a meeting
 *
 * Features:
 * - Shows scheduled time, duration, location
 * - Dancer/organization info based on viewAs prop
 * - Status badge with color coding
 * - Priority indicator
 * - Action buttons for status changes
 */
export function MeetingCard({
  meeting,
  onClick,
  onConfirm,
  onCancel,
  onComplete,
  viewAs = 'organization',
  className,
}: MeetingCardProps) {
  const { t } = useTranslation();

  // `scheduledAt` is a full timestamptz (date + time), so rendering it with
  // toLocale*String is timezone-correct — this is NOT the date-only shift that
  // Critical Rule #19 / `date-safety/no-unsafe-date-locale` guard against.
  const formatDate = (scheduledAt: string) => {
    const date = new Date(scheduledAt);
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (scheduledAt: string) => {
    const date = new Date(scheduledAt);
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: MeetingStatus) => {
    const config: Record<
      MeetingStatus,
      {
        variant: 'default' | 'secondary' | 'destructive' | 'outline';
        label: string;
      }
    > = {
      pending: {
        variant: 'secondary',
        label: t('profiles:meetings.status.pending'),
      },
      confirmed: {
        variant: 'default',
        label: t('profiles:meetings.status.confirmed'),
      },
      cancelled: {
        variant: 'destructive',
        label: t('profiles:meetings.status.cancelled'),
      },
      completed: {
        variant: 'outline',
        label: t('profiles:meetings.status.completed'),
      },
      no_show: {
        variant: 'destructive',
        label: t('profiles:meetings.status.noShow'),
      },
    };
    const { variant, label } = config[status];
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getPriorityBadge = (priority: MeetingPriority) => {
    if (priority === 'high') {
      return (
        <Badge variant="destructive" className="text-xs">
          {t('profiles:meetings.priority.high')}
        </Badge>
      );
    }
    return null;
  };

  const getRequestedByLabel = (requestedBy: MeetingRequestedBy) => {
    return requestedBy === 'organization'
      ? t('profiles:meetings.requestedBy.organization')
      : t('profiles:meetings.requestedBy.dancer');
  };

  const showOtherParty =
    viewAs === 'organization' ? meeting.dancer : meeting.organization;
  const isPast = new Date(meeting.scheduled_at) < new Date();
  const canConfirm = meeting.status === 'pending';
  const canCancel =
    meeting.status === 'pending' || meeting.status === 'confirmed';
  const canComplete = meeting.status === 'confirmed' && isPast;

  return (
    <Card
      className={cn(
        'transition-colors',
        onClick && 'hover:bg-muted/50 cursor-pointer',
        meeting.priority === 'high' && 'border-destructive/50',
        className,
      )}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            {showOtherParty && (
              <Avatar className="h-10 w-10">
                <AvatarImage
                  src={
                    viewAs === 'organization'
                      ? (meeting.dancer?.avatar_url ?? undefined)
                      : (meeting.organization?.logo_url ?? undefined)
                  }
                  alt={
                    viewAs === 'organization'
                      ? `${meeting.dancer?.first_name} ${meeting.dancer?.last_name}`
                      : (meeting.organization?.name ?? '')
                  }
                />
                <AvatarFallback>
                  {viewAs === 'organization' ? (
                    <User className="h-5 w-5" />
                  ) : (
                    <Building2 className="h-5 w-5" />
                  )}
                </AvatarFallback>
              </Avatar>
            )}
            <div>
              <CardTitle className="line-clamp-1 text-base">
                {viewAs === 'organization'
                  ? `${meeting.dancer?.first_name} ${meeting.dancer?.last_name}`
                  : meeting.organization?.name}
              </CardTitle>
              {meeting.event && (
                <p className="text-muted-foreground text-sm">
                  {meeting.event.name}
                </p>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {getPriorityBadge(meeting.priority)}
            {getStatusBadge(meeting.status)}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Date and Time */}
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="text-muted-foreground h-4 w-4 shrink-0" />
            <span>{formatDate(meeting.scheduled_at)}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="text-muted-foreground h-4 w-4 shrink-0" />
            <span>
              {formatTime(meeting.scheduled_at)} ({meeting.duration_minutes}{' '}
              min)
            </span>
          </div>
        </div>

        {/* Location */}
        {meeting.location && (
          <div className="flex items-center gap-2 text-sm">
            <MapPin className="text-muted-foreground h-4 w-4 shrink-0" />
            <span className="truncate">{meeting.location}</span>
          </div>
        )}

        {/* Meeting URL */}
        {meeting.meeting_url && (
          <div className="flex items-center gap-2 text-sm">
            <ExternalLink className="text-muted-foreground h-4 w-4 shrink-0" />
            <a
              href={meeting.meeting_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary truncate hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {t('profiles:meetings.joinMeeting')}
            </a>
          </div>
        )}

        {/* Notes */}
        {meeting.notes && (
          <p className="text-muted-foreground line-clamp-2 text-sm">
            {meeting.notes}
          </p>
        )}

        {/* Requested by */}
        <div className="text-muted-foreground text-xs">
          {getRequestedByLabel(meeting.requested_by)}
        </div>

        {/* Action buttons */}
        {(onConfirm || onCancel || onComplete) && (
          <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
            {onConfirm && canConfirm && (
              <Button size="sm" variant="default" onClick={onConfirm}>
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                {t('profiles:meetings.actions.confirm')}
              </Button>
            )}
            {onComplete && canComplete && (
              <Button size="sm" variant="outline" onClick={onComplete}>
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                {t('profiles:meetings.actions.complete')}
              </Button>
            )}
            {onCancel && canCancel && (
              <Button size="sm" variant="ghost" onClick={onCancel}>
                <XCircle className="mr-1.5 h-4 w-4" />
                {t('profiles:meetings.actions.cancel')}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
